/**
 * JSON-LD for public topic guide pages at /start/:slug.
 *
 * @see https://developers.google.com/search/docs/appearance/structured-data/breadcrumb
 * @see https://developers.google.com/search/docs/appearance/structured-data/speakable
 */
import { buildFaqJsonLd } from '@/utils/topic_page_copy';
import { resolveTopicShareImage } from '@/utils/topic_seo';

const SITE_URL = 'https://gab.ai';

/** CSS selectors for SpeakableSpecification — must match DOM on /start pages. */
export const TOPIC_SPEAKABLE_SELECTORS = [
  '#topic-speakable-headline',
  '#topic-speakable-summary',
  '[data-speakable="topic-overview"]',
];

function absUrl(canonicalPath) {
  if (!canonicalPath || typeof canonicalPath !== 'string') return SITE_URL;
  const path = canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`;
  return `${SITE_URL}${path}`;
}

function listItem(position, name, item) {
  const row = { '@type': 'ListItem', position, name };
  if (item) row.item = item;
  return row;
}

/**
 * Primary trail: Topics → category (optional) → topic.
 * Secondary trail (when tags exist): Topics → tag → topic.
 */
export function buildTopicBreadcrumbLists({
  pageUrl,
  topicName,
  topicsHubName = 'Topics',
  category,
  primaryTag,
}) {
  const topicsUrl = `${SITE_URL}/topics`;
  const trails = [];

  const categorySlug = String(category?.slug || '').trim();
  const categoryName = String(category?.name || '').trim();
  const categoryItems = [
    listItem(1, topicsHubName, topicsUrl),
  ];
  if (categorySlug && categoryName) {
    categoryItems.push(
      listItem(2, categoryName, `${SITE_URL}/topics/category/${encodeURIComponent(categorySlug)}`),
    );
    categoryItems.push(listItem(3, topicName));
  } else {
    categoryItems.push(listItem(2, topicName));
  }
  trails.push({
    '@type': 'BreadcrumbList',
    '@id': `${pageUrl}#breadcrumb-category`,
    itemListElement: categoryItems,
  });

  const tagSlug = String(primaryTag?.slug || '').trim();
  const tagName = String(primaryTag?.name || '').trim();
  if (tagSlug && tagName) {
    const tagItems = [
      listItem(1, topicsHubName, topicsUrl),
      listItem(2, tagName, `${SITE_URL}/tags/${encodeURIComponent(tagSlug)}`),
      listItem(3, topicName),
    ];
    trails.push({
      '@type': 'BreadcrumbList',
      '@id': `${pageUrl}#breadcrumb-tag`,
      itemListElement: tagItems,
    });
  }

  return trails;
}

/**
 * @param {object} opts
 * @param {object} opts.topic
 * @param {string} opts.metaTitle
 * @param {string} opts.metaDescription
 * @param {string} opts.canonicalPath — e.g. `/start/foo`
 * @param {object[]} [opts.pageCopyBlocks]
 * @param {string} [opts.topicsHubName]
 */
export function buildTopicPageStructuredData({
  topic,
  metaTitle,
  metaDescription,
  canonicalPath,
  pageCopyBlocks,
  topicsHubName = 'Topics',
}) {
  if (!topic) return null;

  const pageUrl = absUrl(canonicalPath);
  const topicName = String(topic.name || '').trim() || 'Topic';
  const tags = (Array.isArray(topic.tags) ? topic.tags : [])
    .filter((tag) => tag?.slug && tag?.name);
  const primaryTag = tags[0] || null;

  const primaryImage = resolveTopicShareImage(topic);

  const webPage = {
    '@type': 'WebPage',
    '@id': `${pageUrl}#webpage`,
    url: pageUrl,
    name: metaTitle || topicName,
    headline: topicName,
    description: metaDescription,
    inLanguage: 'en-US',
    isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: SITE_URL },
    breadcrumb: { '@id': `${pageUrl}#breadcrumb-category` },
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: TOPIC_SPEAKABLE_SELECTORS,
    },
    ...(primaryImage ? { primaryImageOfPage: { '@type': 'ImageObject', url: primaryImage } } : {}),
    ...(topic.updatedAt ? { dateModified: topic.updatedAt } : {}),
  };

  const graph = [
    webPage,
    ...buildTopicBreadcrumbLists({
      pageUrl,
      topicName,
      topicsHubName,
      category: topic.category,
      primaryTag,
    }),
  ];

  const faq = buildFaqJsonLd(pageCopyBlocks);
  if (faq?.mainEntity?.length) {
    graph.push({ '@type': 'FAQPage', mainEntity: faq.mainEntity });
  }

  return { '@context': 'https://schema.org', '@graph': graph };
}
